<template>
  <div v-bind="$attrs">
    <client-only>
      <dates-wizard-body
        v-if="availabilityLoaded"
        class="pitch-availability-picker"
        :dates="arriveDepartDates"
        :show-header-range="false"
        :availability="datepickerAvailability"
        :opening-dates="openingDates"
        :calendar-start-date="calendarStartDate"
        :category-details="campsiteCategories"
        :selected-dates-price="selectedDatesPrice"
        :show-asterix="showAsterix"
        @changeRange="$emit('changeRange', $event)"
        @clearDates="$emit('clearDates', $event)"
        @monthChanged="onMonthChange"
        @yearChanged="onYearChange"
        @datesChanged="$emit('datesChanged', $event)"
      >
        <template #pre-header>
          <div class="pitch-availability-cal-nav">
            <pitchtype-mini-availability-calendar
              v-if="!prevMonthInPast && prevMonthData"
              :should-fetch="false"
              :datepicker-availability-results="prevMonthData"
              :stub-data="{ month: prevMonth, year: prevMonthYear }"
              :stay-dates="stayDates"
              :campsite-slug="campsiteSlug"
              :months-to-show="1"
            />
            <pitchtype-mini-availability-calendar
              v-if="nextMonthData"
              :should-fetch="false"
              :datepicker-availability-results="nextMonthData"
              :stay-dates="stayDates"
              :stub-data="{ month: nextMonth, year: nextMonthYear }"
              :campsite-slug="campsiteSlug"
              :months-to-show="1"
            />
          </div>
        </template>
      </dates-wizard-body>
    </client-only>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute } from 'nuxt/app'
import { isEqual } from 'lodash-es'
import { computed, ref, watch } from 'vue'
import {
  calculateFourMonthRange,
  calculateFourMonthRangeForMonth,
} from '~/apps/calendar-unified/dateUtils'
import type { Party } from '~/apps/campsite-details/types'
import { useCalendarStore } from '~/stores/useCalendarStore'
import { IsoDateFormatter } from '~/utility/date/IsoDateFormatter'
import type { SimpleMoney } from '~/utility/money'
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import { DatepickerAvailabilityResultsDay } from '../../../datepicker-availability/types'
import { OpeningDatesRange } from '../../types'
import type { DatepickerAvailabilityArriveDepartIso } from '../components/types'

interface RawOpeningDateRange {
  openingDate: string
  closingDate: string
}

const emit = defineEmits([
  'monthChanged',
  'yearChanged',
  'datesChanged',
  'changeRange',
  'clearDates',
])

const props = withDefaults(
  defineProps<{
    campsiteSlug: string
    pitchtypeId: string
    party: Party
    selectedCategoryIds?: string[]
    campsiteCategories: CampsiteCategory[]
    stayDates: Partial<DatepickerAvailabilityArriveDepartIso>
    minNightStayApplies: boolean
    campsiteOpeningDatesRange: RawOpeningDateRange[]
    calendarStartDate: string
    selectedDatesPrice?: SimpleMoney
    showAsterix?: boolean
  }>(),
  {
    selectedCategoryIds: () => [],
    campsiteCategories: () => [],
    stayDates: () => ({ start: undefined, end: undefined }),
    minNightStayApplies: false,
    showAsterix: false,
  },
)

function resolveCalendarCenterDate(): string | undefined {
  if (props.stayDates.start) {
    return props.stayDates.start
  }

  if (!props.calendarStartDate) {
    return undefined
  }

  const calendarStart = new Date(props.calendarStartDate)
  const today = new Date()
  const isFutureOrCurrentMonth =
    calendarStart.getUTCFullYear() > today.getUTCFullYear() ||
    (calendarStart.getUTCFullYear() === today.getUTCFullYear() &&
      calendarStart.getUTCMonth() >= today.getUTCMonth())

  return isFutureOrCurrentMonth ? props.calendarStartDate : undefined
}

// Calculate 4-month date range centered around arrive date (or today)
// This provides an extra month buffer for smooth forward navigation
const initialCenterDate = resolveCalendarCenterDate()
const { start: startDate, end: endDate } =
  calculateFourMonthRange(initialCenterDate)

const chargetypePeriod = ref({
  start: startDate,
  end: endDate,
})

const route = useRoute()
const lang =
  (Array.isArray(route.params.lang)
    ? route.params.lang[0]
    : route.params.lang) || 'en-gb'
const calendarStore = useCalendarStore()

// Extract facet parameters (can be single value or array)
const facetParam = route.query.facet
const facets: string[] = Array.isArray(facetParam)
  ? facetParam.filter((f): f is string => typeof f === 'string')
  : facetParam && typeof facetParam === 'string'
    ? [facetParam]
    : []

// Component parameters for store
const componentParams = computed(() => ({
  pitchtypeId: props.pitchtypeId,
  campsiteSlug: props.campsiteSlug,
  stayDates: props.stayDates,
  party: {
    adults: props.party.adults,
    dogs: props.party.dogs || 0,
    childAges: props.party.childAges?.slice().sort((a, b) => a - b) || [],
  },
  facets: facets.sort(),
  dateRange: {
    start: IsoDateFormatter.toIsoDate(chargetypePeriod.value.start),
    end: IsoDateFormatter.toIsoDate(chargetypePeriod.value.end),
  },
}))

// Get data from store using high-level interface
// TODO: Add proper return types for store methods to avoid unsafe returns
const cachedDatepickerAvailability = computed(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Store returns untyped calendar data
  () => calendarStore.getComponentData(lang, componentParams.value),
)

// Generate cache key using store method
const cacheKey = computed(
  () =>
    `calendar-main-${calendarStore.generateComponentCacheKey(componentParams.value, lang)}`,
)

const { data } = useAsyncData(
  cacheKey.value,
  async () => {
    // Load data via store's high-level interface
    await calendarStore.loadComponentData(lang, componentParams.value)
    return true
  },
  {
    // Fetch on client only so we leverage calendar store caching and easier debugging
    server: false,
    lazy: false,
    dedupe: 'cancel',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Nuxt payload data is untyped
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

// All data access comes from store for unified API
const datepickerAvailability = computed(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Store returns untyped calendar data
  () => cachedDatepickerAvailability.value,
)

const availabilityLoaded = computed(() => !!data.value)

const prevMonthData = ref<DatepickerAvailabilityResultsDay[]>([])
const nextMonthData = ref<DatepickerAvailabilityResultsDay[]>([])
const prevMonthInPast = computed(() => {
  const today = new Date()
  const currentMonthStart = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1),
  )
  const prevMonthDate = new Date(
    Date.UTC(prevMonthYear.value, prevMonth.value, 1),
  )
  return prevMonthDate < currentMonthStart
})

const initialViewDate = initialCenterDate
  ? new Date(initialCenterDate)
  : new Date()
const currentMonth = ref(initialViewDate.getUTCMonth())
const currentYear = ref(initialViewDate.getUTCFullYear())

// Reset calendar view when pitchtype changes
watch(
  () => props.pitchtypeId,
  (_newPitchtypeId, _oldPitchtypeId) => {
    // Reset calendar to current month when pitchtype changes

    const centerDate = resolveCalendarCenterDate()
    const viewDate = centerDate ? new Date(centerDate) : new Date()
    currentMonth.value = viewDate.getUTCMonth()
    currentYear.value = viewDate.getUTCFullYear()

    // Reset date range to current month
    const { start: newStart, end: newEnd } = calculateFourMonthRange(centerDate)
    chargetypePeriod.value = { start: newStart, end: newEnd }
  },
)

const currentDate = computed(() => {
  const currentDate = new Date(Date.UTC(currentYear.value, currentMonth.value))
  return currentDate
})

const prevDate = computed(() => {
  const prevDate = new Date(currentDate.value)
  prevDate.setUTCMonth(prevDate.getUTCMonth() - 1)
  return prevDate
})

const nextDate = computed(() => {
  const nextDate = new Date(currentDate.value)
  nextDate.setUTCMonth(nextDate.getUTCMonth() + 1)
  return nextDate
})

const prevMonth = computed(() => prevDate.value.getUTCMonth())
const prevMonthYear = computed(() => prevDate.value.getUTCFullYear())
const nextMonth = computed(() => nextDate.value.getUTCMonth())
const nextMonthYear = computed(() => nextDate.value.getUTCFullYear())

const openingDates = computed((): OpeningDatesRange[] => {
  return props.campsiteOpeningDatesRange
    ? mapOpeningDateRanges(props.campsiteOpeningDatesRange)
    : []
})

function onMonthChange(month: number) {
  if (month === currentMonth.value) {
    return
  }

  // Use standardized 4-month calculation centered around the new month
  // This ensures we always have the next+1 month pre-loaded
  const { start, end } = calculateFourMonthRangeForMonth(
    month,
    currentYear.value,
  )

  if (currentMonth.value !== month) {
    currentMonth.value = month
  }

  chargetypePeriod.value = { start, end }
  emit('monthChanged', currentMonth.value)
}

function onYearChange(year: number) {
  if (year === currentYear.value) {
    return
  }

  // Use standardized 4-month calculation centered around the current month in new year
  const { start, end } = calculateFourMonthRangeForMonth(
    currentMonth.value,
    year,
  )

  if (currentYear.value !== year) {
    currentYear.value = year
  }

  chargetypePeriod.value = { start, end }
  emit('yearChanged', currentYear.value)
}

function generateMonthAvailabilityData(
  month: number,
  year: number,
): DatepickerAvailabilityResultsDay[] {
  if (!datepickerAvailability.value) {
    return []
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Store returns untyped calendar data
  return datepickerAvailability.value.reduce(
    (
      res: DatepickerAvailabilityResultsDay[],
      day: DatepickerAvailabilityResultsDay,
    ) => {
      const date = new Date(day.date)
      if (date.getUTCMonth() === month && date.getUTCFullYear() === year) {
        return [...res, day]
      }
      return res
    },
    [] as DatepickerAvailabilityResultsDay[],
  )
}

function mapOpeningDateRanges(
  openingDateRanges: RawOpeningDateRange[],
): OpeningDatesRange[] {
  return openingDateRanges.map((r: RawOpeningDateRange) => {
    return {
      from: r.openingDate,
      to: r.closingDate,
    }
  })
}

const arriveDepartDates = computed(() => {
  return {
    arrive: props.stayDates.start || null,
    depart: props.stayDates.end || null,
  }
})

watch(
  [datepickerAvailability, props.stayDates, currentMonth, currentYear],
  () => {
    prevMonthData.value = generateMonthAvailabilityData(
      prevMonth.value,
      prevMonthYear.value,
    )
    nextMonthData.value = generateMonthAvailabilityData(
      nextMonth.value,
      nextMonthYear.value,
    )
  },
  {
    immediate: true,
  },
)

// Watch for parameter changes to load additional calendar data
if (import.meta.client) {
  watch(
    componentParams,
    async (newParams, oldParams) => {
      if (!oldParams || isEqual(newParams, oldParams)) {
        return
      }

      // Ask store if it has the required data
      if (!calendarStore.hasComponentData(lang, newParams)) {
        await calendarStore.loadComponentData(lang, newParams)
      }
    },
    { deep: true },
  )
}

// Expose datepickerAvailability for parent component template ref access
defineExpose({
  datepickerAvailability,
})
</script>

<style scoped>
.pitch-availability-picker {
  position: relative;
}

.pitch-availability-picker :deep(.dayContainer .flatpickr-day) {
  border-width: 1.5px;
  border-color: #eef2f7;
  border-radius: 5px;
  height: auto;
  min-height: 2.75em;
  line-height: 1;
  font-weight: normal;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  padding: 3px 0 3px 6px;
}

.pitch-availability-picker
  :deep(.flatpickr-day.nextMonthDay.selected.startRange),
.pitch-availability-picker
  :deep(.flatpickr-day.prevMonthDay.selected.startRange),
.pitch-availability-picker :deep(.flatpickr-day.selected.startRange) {
  border-left: 1.5px solid #eef2f7;
}

.pitch-availability-picker :deep(.flatpickr-day.startRange),
.pitch-availability-picker :deep(.flatpickr-day.startRange.inRange),
.pitch-availability-picker :deep(.flatpickr-day.startRange:hover),
.pitch-availability-picker :deep(.flatpickr-calendar .flatpickr-day.selected) {
  border-left: 1.5px solid #eef2f7;
  border-right: none;
  border-color: #eef2f7;
}

.pitch-availability-picker :deep(.flatpickr-day.endRange),
.pitch-availability-picker :deep(.flatpickr-day.endRange.inRange),
.pitch-availability-picker :deep(.flatpickr-day.endRange:hover),
.pitch-availability-picker :deep(.flatpickr-day.selected.endRange) {
  border-right: 1.5px solid #eef2f7;
  border-left: none;
}

.pitch-availability-picker :deep(.flatpickr-day.inRange) {
  border-radius: 0;
}

.pitch-availability-picker :deep(.flatpickr-day.selected.startRange),
.pitch-availability-picker :deep(.flatpickr-day.startRange.startRange),
.pitch-availability-picker :deep(.flatpickr-day.endRange.startRange) {
  border-radius: 5px 0 0 5px;
}

.pitch-availability-picker :deep(.flatpickr-day.selected.endRange),
.pitch-availability-picker :deep(.flatpickr-day.startRange.endRange),
.pitch-availability-picker :deep(.flatpickr-day.endRange.endRange) {
  border-radius: 0 5px 5px 0;
}

.pitch-availability-picker :deep(.flatpickr-day.date-unavailable .quote-price),
.pitch-availability-picker :deep(.flatpickr-day.date-unavailable .quote-nights),
.pitch-availability-picker :deep(.flatpickr-day.inRange .quote-price),
.pitch-availability-picker :deep(.flatpickr-day.inRange .quote-nights),
.pitch-availability-picker :deep(.flatpickr-day.endRange .quote-price),
.pitch-availability-picker :deep(.flatpickr-day.endRange .quote-nights) {
  display: none;
}

.pitch-availability-picker :deep(.quote-nights),
.pitch-availability-picker :deep(.quote-price) {
  align-self: center;
  margin-left: -6px;
}

.pitch-availability-picker :deep(.quote-price) {
  font-weight: bold;
  margin-top: 0.3em;
  font-size: var(--small);
  letter-spacing: -0.05em;
}

.pitch-availability-picker :deep(.quote-nights) {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.2em;
  font-size: var(--tiny);
}

.pitch-availability-picker :deep(.quote-nights:after) {
  content: '';
  background: url('~/assets/images/nights.svg') center center no-repeat;
  background-size: contain;
  display: inline-flex;
  height: 0.8em;
  width: 0.8em;
  opacity: 0.8;
}

.pitch-availability-picker
  :deep(.flatpickr-day.flatpickr-disabled.date-unavailable),
.pitch-availability-picker
  :deep(.flatpickr-day.flatpickr-disabled.date-unavailable:hover) {
  background-color: #eef2f7;
  box-shadow: inset 0 0 0 1px var(--colour-page-background);
}

.pitch-availability-picker :deep(.flatpickr-months) {
  height: 4em;
  margin-bottom: 0.8em;
}

.pitch-availability-picker :deep(span.flatpickr-weekday) {
  font-weight: normal;
}

.pitch-availability-picker :deep(.flatpickr-month),
.pitch-availability-picker :deep(.flatpickr-innerContainer),
.pitch-availability-picker :deep(.flatpickr-weekdaycontainer),
.pitch-availability-picker :deep(.flatpickr-weekdays),
.pitch-availability-picker :deep(span.flatpickr-weekday),
.pitch-availability-picker :deep(.flatpickr-month),
.pitch-availability-picker :deep(.flatpickr-current-month span.cur-month:hover),
.pitch-availability-picker
  :deep(.flatpickr-current-month .numInputWrapper:hover) {
  background-color: transparent;
}

.pitch-availability-picker :deep(.flatpickr-prev-month:active::before),
.pitch-availability-picker :deep(.flatpickr-next-month:active::before) {
  content: initial;
}

.pitch-availability-picker :deep(.flatpickr-prev-month.flatpickr-disabled),
.pitch-availability-picker :deep(.flatpickr-next-month.flatpickr-disabled) {
  display: flex;
  justify-content: center;
  align-content: center;
}

.pitch-availability-picker :deep(.flatpickr-prev-month),
.pitch-availability-picker :deep(.flatpickr-next-month) {
  height: 2em;
  padding: 2.5em 0;
  margin-top: 0;
}

.pitch-availability-picker :deep(.flatpickr-prev-month > img),
.pitch-availability-picker :deep(.flatpickr-next-month > img) {
  width: 1.5em;
  height: 1.5em;
}

.pitch-availability-picker :deep(.flatpickr-prev-month) {
  padding-left: 5.25em;
}

.pitch-availability-picker :deep(.flatpickr-next-month) {
  padding-right: 5.25em;
}

.pitch-availability-picker :deep(.clear-dates),
.pitch-availability-picker :deep(.arrowUp),
.pitch-availability-picker :deep(.arrowDown) {
  display: none;
}

.pitch-availability-picker :deep(.flatpickr-current-month) {
  font-size: var(--body);
}

.pitch-availability-picker :deep(.flatpickr-current-month input.cur-year) {
  font-size: inherit;
}

.pitch-availability-picker :deep(.flatpickr-current-month .numInputWrapper) {
  margin-top: 0;
}

.pitch-availability-cal-nav {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.pitch-availability-cal-nav :deep(.calendar:last-of-type) {
  margin-left: auto;
}

.pitch-availability-cal-nav :deep(.day) {
  width: 8px;
  height: 8px;
}

.pitch-availability-cal-nav :deep(.title) {
  color: var(--colour-dark-blue);
  font-size: var(--small);
  margin-bottom: 6px;
}

.pitch-availability-cal-nav :deep(.calendar:last-of-type .title) {
  text-align: right;
}
</style>
